<template>
  <Head title="Notifications" />

  <div class="py-6 px-4 sm:px-6 lg:px-8">
    <div class="max-w-4xl mx-auto space-y-6">
      <!-- Page Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
            Notifications
          </h1>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Stay up to date with your notifications
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-wrap gap-2">
          <Button
            v-if="unreadCount > 0"
            @click="markAllAsRead"
            size="sm"
            :loading="isMarkingAllRead"
            :disabled="isMarkingAllRead"
          >
            <CheckIcon class="h-4 w-4 mr-1" />
            Mark all as read
          </Button>

          <Button
            v-if="readCount > 0"
            @click="deleteAllRead"
            size="sm"
            variant="secondary"
            :loading="isDeletingAllRead"
            :disabled="isDeletingAllRead"
          >
            <TrashIcon class="h-4 w-4 mr-1" />
            Clear read
          </Button>
        </div>
      </div>

      <!-- Filters Card -->
      <Card>
        <div class="space-y-4">
          <!-- Tabs -->
          <div class="flex flex-wrap gap-2">
            <button
              v-for="tab in tabs"
              :key="tab.value"
              @click="changeTab(tab.value)"
              type="button"
              :class="[
                'px-4 py-2 rounded-lg text-sm font-medium transition',
                activeTab === tab.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ]"
            >
              {{ tab.label }}
              <span v-if="tab.count !== undefined" class="ml-2 opacity-75">
                ({{ tab.count }})
              </span>
            </button>
          </div>

          <!-- Category Filter -->
          <div class="flex flex-col sm:flex-row gap-4">
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category
              </label>
              <Select
                v-model="selectedCategory"
                :options="categoryOptions"
                placeholder="All categories"
                @update:model-value="applyFilters"
              />
            </div>
          </div>
        </div>
      </Card>

      <!-- Loading State -->
      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="groupedNotifications.length === 0"
        class="text-center py-16"
      >
        <BellSlashIcon class="h-20 w-20 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
        <h3 class="text-xl font-medium text-gray-900 dark:text-white mb-2">
          No notifications
        </h3>
        <p class="text-gray-500 dark:text-gray-400">
          {{ getEmptyStateMessage() }}
        </p>
      </div>

      <!-- Notifications List -->
      <div v-else class="space-y-6">
        <div
          v-for="group in groupedNotifications"
          :key="group.label"
          class="space-y-3"
        >
          <!-- Date Group Header -->
          <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {{ group.label }}
          </h3>

          <!-- Notifications in Group -->
          <Card class="divide-y divide-gray-200 dark:divide-gray-700 overflow-hidden">
            <div
              v-for="notification in group.notifications"
              :key="notification.id"
              :class="[
                'flex items-start p-4 transition-colors',
                !notification.read_at
                  ? 'bg-blue-50 dark:bg-blue-900/20'
                  : 'bg-white dark:bg-gray-800',
                'hover:bg-gray-50 dark:hover:bg-gray-700'
              ]"
            >
              <!-- Icon -->
              <div class="flex-shrink-0 mr-4">
                <div
                  :class="[
                    'p-2.5 rounded-lg',
                    getNotificationIconBgColor(notification.data.category)
                  ]"
                >
                  <component
                    :is="getNotificationIcon(notification.data.category)"
                    :class="[
                      'h-6 w-6',
                      getNotificationIconColor(notification.data.category)
                    ]"
                  />
                </div>
              </div>

              <!-- Content -->
              <div
                class="flex-1 min-w-0 cursor-pointer"
                @click="handleNotificationClick(notification)"
              >
                <div class="flex items-start justify-between gap-4">
                  <div class="flex-1 min-w-0">
                    <!-- Title -->
                    <p class="font-medium text-gray-900 dark:text-white">
                      {{ notification.data.title }}
                    </p>

                    <!-- Message -->
                    <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {{ notification.data.message }}
                    </p>

                    <!-- Meta Info -->
                    <div class="flex items-center flex-wrap gap-2 mt-2">
                      <p class="text-xs text-gray-500 dark:text-gray-500">
                        {{ notification.created_at_human }}
                      </p>

                      <!-- Priority Badge -->
                      <span
                        v-if="notification.data.priority === 'urgent'"
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                      >
                        <ExclamationCircleIcon class="h-3 w-3 mr-1" />
                        Urgent
                      </span>
                      <span
                        v-else-if="notification.data.priority === 'high'"
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                      >
                        High Priority
                      </span>

                      <!-- Category Badge -->
                      <span
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                      >
                        {{ getCategoryLabel(notification.data.category) }}
                      </span>
                    </div>

                    <!-- Action Link -->
                    <div
                      v-if="notification.data.action_text && notification.data.action_url"
                      class="mt-3"
                    >
                      <span class="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
                        {{ notification.data.action_text }} →
                      </span>
                    </div>
                  </div>

                  <!-- Unread Indicator -->
                  <div v-if="!notification.read_at" class="flex-shrink-0">
                    <div class="h-2.5 w-2.5 bg-blue-600 dark:bg-blue-500 rounded-full"></div>
                  </div>
                </div>
              </div>

              <!-- Delete Button -->
              <button
                @click.stop="confirmDeleteNotification(notification.id)"
                type="button"
                class="flex-shrink-0 ml-4 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                :aria-label="'Delete notification: ' + notification.data.title"
              >
                <TrashIcon class="h-5 w-5" />
              </button>
            </div>
          </Card>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="hasMore && !isLoading" class="text-center pt-4">
        <Button
          @click="loadMore"
          variant="secondary"
          :loading="isLoadingMore"
          :disabled="isLoadingMore"
        >
          Load More
        </Button>
      </div>

      <!-- Pagination Info -->
      <div
        v-if="notifications.data && notifications.data.length > 0"
        class="text-center text-sm text-gray-500 dark:text-gray-400"
      >
        Showing {{ notifications.from }} to {{ notifications.to }} of {{ notifications.total }} notifications
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { Head, router } from '@inertiajs/vue3';
import {
  BellSlashIcon,
  TrashIcon,
  CheckIcon,
  TicketIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  AtSymbolIcon,
  BuildingOfficeIcon,
  CogIcon,
  ShieldExclamationIcon,
  BellIcon,
  ExclamationCircleIcon,
} from '@heroicons/vue/24/outline';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Select from '@/Components/Common/Select.vue';
import { useToast } from '@/Composables/useToast';
import axios from 'axios';
import route from '@/Utils/route';

// Props from controller
const props = defineProps({
  notifications: {
    type: Object,
    required: true,
  },
  pagination: {
    type: Object,
    required: true,
  },
  filters: {
    type: Object,
    required: true,
  },
  unreadCount: {
    type: Number,
    default: 0,
  },
  categories: {
    type: Array,
    default: () => [],
  },
});

// Composables
const { success, error: showError } = useToast();

// State
const isLoading = ref(false);
const isLoadingMore = ref(false);
const isMarkingAllRead = ref(false);
const isDeletingAllRead = ref(false);
const activeTab = ref(props.filters.filter || 'all');
const selectedCategory = ref(props.filters.category || null);

// Computed
const tabs = computed(() => [
  {
    label: 'All',
    value: 'all',
    count: props.pagination.total,
  },
  {
    label: 'Unread',
    value: 'unread',
    count: props.unreadCount,
  },
  {
    label: 'Read',
    value: 'read',
    count: props.pagination.total - props.unreadCount,
  },
]);

const readCount = computed(() => {
  return props.pagination.total - props.unreadCount;
});

const categoryOptions = computed(() => {
  return [
    { value: null, label: 'All Categories' },
    ...props.categories.map(cat => ({
      value: cat.value,
      label: cat.label,
    })),
  ];
});

const groupedNotifications = computed(() => {
  if (!props.notifications || props.notifications.length === 0) {
    return [];
  }

  const groups = {
    today: { label: 'Today', notifications: [] },
    yesterday: { label: 'Yesterday', notifications: [] },
    thisWeek: { label: 'This Week', notifications: [] },
    earlier: { label: 'Earlier', notifications: [] },
  };

  props.notifications.forEach(notification => {
    const groupKey = getDateGroupKey(notification.created_at);
    if (groups[groupKey]) {
      groups[groupKey].notifications.push(notification);
    }
  });

  // Filter out empty groups
  return Object.values(groups).filter(group => group.notifications.length > 0);
});

const hasMore = computed(() => {
  return props.pagination.current_page < props.pagination.last_page;
});

// Methods
const changeTab = (tab) => {
  activeTab.value = tab;
  applyFilters();
};

const applyFilters = () => {
  router.get(
    route('notifications.index'),
    {
      filter: activeTab.value,
      category: selectedCategory.value,
    },
    {
      preserveState: false,
      preserveScroll: false,
    }
  );
};

const handleNotificationClick = async (notification) => {
  try {
    // Mark as read if unread
    if (!notification.read_at) {
      await axios.post(route('notifications.markAsRead', notification.id));
    }

    // Navigate to action URL if exists
    if (notification.data.action_url) {
      router.visit(notification.data.action_url);
    }
  } catch (err) {
    console.error('Failed to handle notification click:', err);
    showError('Failed to process notification');
  }
};

const markAllAsRead = async () => {
  try {
    isMarkingAllRead.value = true;

    await axios.post(route('notifications.markAllRead'));

    router.reload({
      preserveScroll: true,
      onSuccess: () => {
        success('All notifications marked as read');
      },
    });
  } catch (err) {
    console.error('Failed to mark all as read:', err);
    showError('Failed to mark notifications as read');
  } finally {
    isMarkingAllRead.value = false;
  }
};

const deleteAllRead = async () => {
  if (!confirm('Are you sure you want to delete all read notifications? This action cannot be undone.')) {
    return;
  }

  try {
    isDeletingAllRead.value = true;

    await axios.delete(route('notifications.destroyAll'));

    router.reload({
      preserveScroll: true,
      onSuccess: () => {
        success('Read notifications deleted successfully');
      },
    });
  } catch (err) {
    console.error('Failed to delete all read:', err);
    showError('Failed to delete notifications');
  } finally {
    isDeletingAllRead.value = false;
  }
};

const confirmDeleteNotification = async (notificationId) => {
  if (!confirm('Delete this notification?')) {
    return;
  }

  try {
    await axios.delete(route('notifications.destroy', notificationId));

    router.reload({
      preserveScroll: true,
      onSuccess: () => {
        success('Notification deleted');
      },
    });
  } catch (err) {
    console.error('Failed to delete notification:', err);
    showError('Failed to delete notification');
  }
};

const loadMore = () => {
  isLoadingMore.value = true;

  router.get(
    route('notifications.index'),
    {
      filter: activeTab.value,
      category: selectedCategory.value,
      page: props.pagination.current_page + 1,
    },
    {
      preserveState: true,
      preserveScroll: true,
      onFinish: () => {
        isLoadingMore.value = false;
      },
    }
  );
};

const getDateGroupKey = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const notificationDate = new Date(date);
  notificationDate.setHours(0, 0, 0, 0);

  if (notificationDate.getTime() === today.getTime()) {
    return 'today';
  } else if (notificationDate.getTime() === yesterday.getTime()) {
    return 'yesterday';
  } else if (date > weekAgo) {
    return 'thisWeek';
  } else {
    return 'earlier';
  }
};

const getEmptyStateMessage = () => {
  if (activeTab.value === 'unread') {
    return "You're all caught up! No unread notifications.";
  } else if (activeTab.value === 'read') {
    return 'No read notifications yet.';
  } else if (selectedCategory.value) {
    return `No notifications in this category.`;
  } else {
    return "You don't have any notifications yet.";
  }
};

const getNotificationIcon = (category) => {
  const icons = {
    tickets: TicketIcon,
    work_orders: WrenchScrewdriverIcon,
    incidents: ExclamationTriangleIcon,
    team: UserGroupIcon,
    mentions: AtSymbolIcon,
    company: BuildingOfficeIcon,
    system: ShieldExclamationIcon,
  };

  return icons[category] || BellIcon;
};

const getNotificationIconColor = (category) => {
  const colors = {
    tickets: 'text-blue-600 dark:text-blue-400',
    work_orders: 'text-green-600 dark:text-green-400',
    incidents: 'text-red-600 dark:text-red-400',
    team: 'text-purple-600 dark:text-purple-400',
    mentions: 'text-yellow-600 dark:text-yellow-400',
    company: 'text-indigo-600 dark:text-indigo-400',
    system: 'text-gray-600 dark:text-gray-400',
  };

  return colors[category] || 'text-gray-600 dark:text-gray-400';
};

const getNotificationIconBgColor = (category) => {
  const colors = {
    tickets: 'bg-blue-100 dark:bg-blue-900/30',
    work_orders: 'bg-green-100 dark:bg-green-900/30',
    incidents: 'bg-red-100 dark:bg-red-900/30',
    team: 'bg-purple-100 dark:bg-purple-900/30',
    mentions: 'bg-yellow-100 dark:bg-yellow-900/30',
    company: 'bg-indigo-100 dark:bg-indigo-900/30',
    system: 'bg-gray-100 dark:bg-gray-700',
  };

  return colors[category] || 'bg-gray-100 dark:bg-gray-700';
};

const getCategoryLabel = (category) => {
  const labels = {
    tickets: 'Tickets',
    work_orders: 'Work Orders',
    incidents: 'Incidents',
    team: 'Team',
    mentions: 'Mentions',
    company: 'Company',
    system: 'System',
  };

  return labels[category] || category;
};
</script>
